import { useState } from "react";
import { Alert, Button, Card, Col, Form, Input, Modal, Popconfirm, Row, Tag, Typography } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const { Title, Text } = Typography;

interface FormValues {
  recipients: string[];
  subject: string;
  content: string;
}

const SendMail = () => {
  const [form] = Form.useForm<FormValues>();
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [recipientInput, setRecipientInput] = useState<string>("");
  const [recipients, setRecipients] = useState<string[]>([]);

  // Watch form values to determine if send button should be disabled
  const subject = Form.useWatch("subject", form);
  const content = Form.useWatch("content", form);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const hasTextContent = (html: string): boolean => {
    const text = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();
    return text.length > 0;
  };

  const isFormValid = subject && content && hasTextContent(content) && recipients.length > 0;

  const validateEmail = (email: string): boolean => {
    return emailRegex.test(email.trim());
  };

  const handleRecipientInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // Check if value contains semicolons or commas (pasted list)
    if (value.includes(";") || value.includes(",")) {
      // Split by semicolon or comma and process all emails
      const emails = value
        .split(/[;,]/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      const validEmails: string[] = [];
      emails.forEach(email => {
        if (validateEmail(email) && !recipients.includes(email)) {
          validEmails.push(email);
        }
      });

      if (validEmails.length > 0) {
        setRecipients([...recipients, ...validEmails]);
        setRecipientInput("");
      } else {
        // If no valid emails found, keep the input as is
        setRecipientInput(value);
      }
    } else {
      // Normal typing - check if user pressed semicolon or comma at the end
      setRecipientInput(value);
      if (value.endsWith(";") || value.endsWith(",")) {
        const email = value.slice(0, -1).trim();
        if (email && validateEmail(email)) {
          if (!recipients.includes(email)) {
            setRecipients([...recipients, email]);
            setRecipientInput("");
          } else {
            setRecipientInput("");
          }
        }
      }
    }
  };

  const handleRecipientInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const email = recipientInput.trim();
      if (email && validateEmail(email)) {
        if (!recipients.includes(email)) {
          setRecipients([...recipients, email]);
          setRecipientInput("");
        } else {
          setRecipientInput("");
        }
      }
    }
  };

  const handleRemoveRecipient = (emailToRemove: string) => {
    setRecipients(recipients.filter(email => email !== emailToRemove));
  };

  const handlePreview = async () => {
    try {
      const values = await form.validateFields(["subject", "content"]);
      if (recipients.length === 0) {
        message("warning", "Legg til minst én mottaker");
        return;
      }
      setLoading(true);
      const response = await axios.post("/send-email/preview/", {
        subject: values.subject,
        content: values.content,
      });
      setPreviewHtml(response.data.html);
      setIsPreviewVisible(true);
    } catch (error) {
      if (error && typeof error === "object" && "errorFields" in error) {
        // Form validation error
        return;
      }
      message("error", "Kunne ikke laste forhåndsvisning");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      const values = await form.validateFields();

      if (recipients.length === 0) {
        message("warning", "Legg til minst én mottaker");
        return;
      }

      // Validate all recipients
      const invalidEmails = recipients.filter(email => !validateEmail(email));
      if (invalidEmails.length > 0) {
        message("error", `Ugyldige e-postadresser: ${invalidEmails.join(", ")}`);
        return;
      }

      setLoading(true);
      await axios.post("/send-email/", {
        subject: values.subject,
        content: values.content,
        recipients,
      });
      message("success", `E-post sendt til ${recipients.length} mottakere`);
      form.resetFields();
      setRecipients([]);
      setRecipientInput("");
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      message("error", errorMessage || "Kunne ikke sende e-post");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card style={{ width: "100%" }}>
        <Title level={2}>Send e-post</Title>

        <Form form={form} layout="vertical">
          <Alert
            message="E-posten sendes fra no-reply@bindeleddet.no"
            type="info"
            showIcon
            style={{ marginBottom: "16px" }}
          />

          <Form.Item
            label="Mottakere"
            required
            help="Skriv inn e-postadresser og separer med semikolon (;) eller trykk Enter"
          >
            <Input
              placeholder="epost@example.com; epost2@example.com"
              value={recipientInput}
              onChange={handleRecipientInputChange}
              onKeyDown={handleRecipientInputKeyDown}
              style={{ marginBottom: "8px" }}
            />
            {recipients.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                {recipients.map(email => (
                  <Tag
                    key={email}
                    closable
                    onClose={() => handleRemoveRecipient(email)}
                    style={{ marginBottom: "4px" }}
                  >
                    {email}
                  </Tag>
                ))}
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Text>
              <strong>{recipients.length}</strong> mottakere vil motta denne e-posten{" "}
              <Text type="secondary">(alle på blindkopi)</Text>
            </Text>
          </Form.Item>

          <Form.Item label="Emne" name="subject" rules={[{ required: true, message: "Emne er påkrevd" }]}>
            <Input placeholder="E-post emne" />
          </Form.Item>

          <Form.Item
            label="Melding"
            name="content"
            rules={[{ required: true, message: "Melding er påkrevd" }]}
            getValueFromEvent={value => value}
          >
            <ReactQuill theme="snow" placeholder="Skriv e-post innhold her..." />
          </Form.Item>

          <Form.Item>
            <Row gutter={8}>
              <Col>
                <Button onClick={handlePreview} loading={loading} disabled={!isFormValid}>
                  Forhåndsvisning
                </Button>
              </Col>
              <Col>
                <Popconfirm
                  title={
                    <>
                      <div>Er du sikker på at du vil sende e-post?</div>
                      <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                        Dette vil sende e-post til {recipients.length} mottakere.
                      </div>
                    </>
                  }
                  onConfirm={handleSend}
                  okText="Send"
                  cancelText="Avbryt"
                  okType="primary"
                >
                  <Button type="primary" loading={loading} size="large" disabled={!isFormValid}>
                    Send e-post
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        title="Forhåndsvisning av e-post"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsPreviewVisible(false)}>
            Lukk
          </Button>,
        ]}
        width={800}
      >
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Modal>
    </>
  );
};

export default SendMail;
